// Simple enum that determines the sorting method for payment methods in Express checkout.
// The higher the number (or eligibility), the further towards the beginning of the method display
//  that method will be shown. Currently this is only used based on eligibility (as per the naming).
enum GatewayEligibility {
	NotEligible = 0,
	Eligible = 1,
	EligibleWithChange = 2,
	EligibleButErrored = 3,
}

interface IPaymentConfiguration {
	selectedGateway: string;
	availableGateways: string[];
	gatewayAvailabilityDetails: Record<string, GatewayEligibilityDetails>;

	gatewayConfigurations: Record<string, GatewayConfiguration>;
}

interface GatewayConfiguration {
	/**
	 * Name to display for Payment Method
	 * @example "Klarna"
	 */
	name: string;
	/**
	 * Optional short description on how the payment method works.
	 * @example "After selecting pay you will be redirected to complete your payment."
	 */
	description: string;

	/**
	 * The WC gateway to use. All peachpay custom gateways are prefixed with 'peachpay_'.
	 */
	gatewayId: string;
	/**
	 * Branding assets to display with the option.
	 */
	assets: {
		title?: ImageOptions;
		badge: ImageOptions;
	};

	/**
     * A boolean to dynamically indicate if they payment method is supported by the browser. If undefined the method is considered supported by default.
     */
	browser?: boolean;

	/**
	 * A boolean to dynamically indicate if the payment method successfully initialized.
     *
     * If `undefined`(default) or `true` the method is considered initialized.
     * If `false` then the payment method is considered errored.
	 */
	initialized?: boolean;

	/**
	 * Does this gateway use a custom place order button?
	 */
	hasCustomButton?: boolean;
}

type ImageOptions = {
	src: string;
	scale?: number;
	translateX?: number;
};

type EligibilityDetail = {
	explanation: string;
};

/**
 * Details why a gateway is not eligible. Properties are only defined if something is wrong.
 */
type GatewayEligibilityDetails = {
	/**
     * Defined by the backend.
     */

	enabled?: EligibilityDetail;
	setup?: EligibilityDetail;
	user?: EligibilityDetail;
	location?: EligibilityDetail;
	minimum?: EligibilityDetail & {minimum: number};
	maximum?: EligibilityDetail & {maximum: number};
	country?: EligibilityDetail & {available_options?: string[]};
	currency?: EligibilityDetail & {available_options?: string[]; fallback_option?: string};

	/**
     * Defined by the frontend.
     */

	browser?: EligibilityDetail;
	initialized?: EligibilityDetail;
};

export {
	type IPaymentConfiguration,
	type GatewayEligibilityDetails,
	type GatewayConfiguration,

	GatewayEligibility,
};
